/**
 * VVDN AP SKU profiles for planning — TX defaults, bands, and cell sizing.
 * Used when placing APs from inventory; not a full antenna pattern library.
 */

import type { ApMountType } from './types'

export type ApModelProfile = {
  model: string
  label: string
  bandsGHz: number[]
  maxTxDbm: number
  defaultTxDbm: number
  defaultMount: ApMountType
  /** Multiplier on typical cell radius in canvas rendering. */
  cellRadiusScale: number
}

export const AP_MODEL_PROFILES: ApModelProfile[] = [
  {
    model: 'VVDN-WiFi-6E',
    label: 'WiFi 6E tri-band',
    bandsGHz: [2.4, 5, 6],
    maxTxDbm: 23,
    defaultTxDbm: 17,
    defaultMount: 'ceiling',
    cellRadiusScale: 1.05,
  },
  {
    model: 'VVDN-WiFi-6',
    label: 'WiFi 6 dual-band',
    bandsGHz: [2.4, 5],
    maxTxDbm: 22,
    defaultTxDbm: 16,
    defaultMount: 'ceiling',
    cellRadiusScale: 1,
  },
  {
    model: 'VVDN-WiFi-6-Outdoor',
    label: 'WiFi 6 outdoor',
    bandsGHz: [2.4, 5],
    maxTxDbm: 24,
    defaultTxDbm: 18,
    defaultMount: 'outdoor',
    cellRadiusScale: 1.15,
  },
]

const DEFAULT_PROFILE = AP_MODEL_PROFILES[1]

export function profileForModel(model?: string | null): ApModelProfile {
  if (!model) return DEFAULT_PROFILE
  const key = model.trim().toLowerCase()
  return AP_MODEL_PROFILES.find(p => p.model.toLowerCase() === key) ?? DEFAULT_PROFILE
}

export function profileForInventoryDevice(device?: { model?: string; name?: string } | null): ApModelProfile {
  if (device?.model) return profileForModel(device.model)
  const name = String(device?.name ?? '').toLowerCase()
  if (name.includes('outdoor') || name.includes('terrace')) return profileForModel('VVDN-WiFi-6-Outdoor')
  if (name.includes('6e') || name.includes('exec') || name.includes('conference')) {
    return profileForModel('VVDN-WiFi-6E')
  }
  return DEFAULT_PROFILE
}

export function bandLabelFromProfile(profile: ApModelProfile, activeBandGHz: number): string {
  if (profile.bandsGHz.includes(activeBandGHz)) return `${activeBandGHz} GHz`
  return `${profile.bandsGHz[profile.bandsGHz.length - 1] ?? 5} GHz`
}

export function apDefaultsFromInventory(
  device: { model?: string; name?: string } | null | undefined,
  bandGHz: number,
): { txPowerDbm: number; mount: ApMountType; mountHeightM: number; band: string } {
  const profile = profileForInventoryDevice(device ?? undefined)
  const band = bandLabelFromProfile(profile, bandGHz)
  return {
    txPowerDbm: profile.defaultTxDbm,
    mount: profile.defaultMount,
    mountHeightM: profile.defaultMount === 'desk' ? 1.2 : profile.defaultMount === 'outdoor' ? 4 : 2.7,
    band,
  }
}
